import {Injectable} from '@angular/core';
import {CommercialModel} from '@app/model/dcp-config-service/commercial-model-config';
import {DcpFacetValue} from '@app/model/dcp-facet-value';
import {DcpReviews} from '@app/model/dcp-reviews';
import {CountrySelectorService} from '@app/services/country-selector.service';
import {FacetSearchService} from '@app/services/facet-search.service';
import {
  CanonicalId,
  ConfigService,
  DcpBundleTemplate,
  JsonService,
  ProductUtilitiesService,
  TypeValuePair
} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Image} from '@spartacus/core';
import {Facet} from '@spartacus/core/src/model/product-search.model';
import {Feature, Product} from '@spartacus/core/src/model/product.model';
import {first} from 'rxjs/operators';
import {PlansAndPricingDataService} from '../details/plans-and-pricing/plans-and-pricing-data.service';
import {CrmAccountTrackingService} from '@app/services/crm-account-tracking.service';
import {CampaignTrackingService} from '@app/services/campaign-tracking.service';

const EXTERNAL_URL: string = 'externalUrl';
const PDP_TARGET_URL_NAME_FEATURE: string = 'PDP target URL';
const CERTIFICATION_NAME_FEATURE: string = 'Certifications';
const CERTIFICATION_NAME_FACET: string = 'Certification';
const PRODUCT_ASSET_TYPE_NAME: string = 'Product Asset Type';
const MEDIUM_DESCRIPTION_NAME_FEATURE: string = 'Medium Description';
const WORKS_WITH_NAME_FEATURE: string = 'Works with';
const SOLUTION_TYPES_NAME_FEATURE: string = 'Solution Types';
export const META_KEYWORDS_FEATURE: string = 'Meta Keywords';
const REVIEWS_DATA_SECTION_ID: string = 'reviews';
const SAP_PRODUCT_ASSET_TYPE: string = 'SAPProduct';
const SEPARATOR: string = '_';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class DcpProductUtilitiesService extends ProductUtilitiesService {
  private backendFacets: Facet[];

  constructor(
    jsonService: JsonService,
    private facetService: FacetSearchService,
    private configService: ConfigService,
    private plansAndPricingDataService: PlansAndPricingDataService,
    private countrySelectorService: CountrySelectorService,
    private crmAccountTrackingService: CrmAccountTrackingService,
    private campaignTrackingService: CampaignTrackingService
  ) {
    super(jsonService);
    this.facetService.backendFacets.pipe(first(), untilDestroyed(this))
      .subscribe((backendFacets: Facet[]) => this.backendFacets = backendFacets);
  }

  /**
   * Get the product asset type. Return value only for SAP Products
   *
   * @param product the product where to search for the product asset type
   */
  getProductType(product: Product): string {
    return this.getFeatureForProduct(product, PRODUCT_ASSET_TYPE_NAME);
  }

  isSapProduct(product: Product): boolean {
    return this.getProductType(product) === SAP_PRODUCT_ASSET_TYPE;
  }

  /**
   * Check if the product's has a Trial transaction type
   *
   * @param product the product where to search for transaction types
   */
  hasFreeTrialTransactionType(product: Product): boolean {
    return product.trial && product.trial.includes('LEAD_TRIAL');
  }

  /**
   * Get the product's certifications.
   *
   * @param product the product where to search for classifications
   */
  getCertification(product: Product): string {
    return this.parseFeatureAsArray(this.getFeatureForProduct(product, CERTIFICATION_NAME_FEATURE)).pop();
  }

  /**
   * Get the product's pdpTargetUrl.
   *
   * @param product the product where to search for classifications
   */
  getPdpTargetUrl(product: Product): string {
    let targetUrl = this.getFeatureForProduct(product, PDP_TARGET_URL_NAME_FEATURE);

    if (!targetUrl || !this.isValidUrl(targetUrl)) {
      return '';
    }

    let target = new URL(targetUrl);

    const countryCode = this.countrySelectorService.getCurrentCountry().isocode;

    if (countryCode) {
      target.searchParams.set('countryCode', countryCode);
    }

    return target.toString();
  }

  /**
   * Retrieve the medium description from a product's classifications
   *
   * @param product the product where to search for the description
   */
  getMediumDescription(product: Product): string {
    return this.getFeatureForProduct(product, MEDIUM_DESCRIPTION_NAME_FEATURE);
  }

  /**
   * Retrieve the works with array from a product's classifications
   *
   * @param product the product where to search for the description
   */
  getWorksWith(product: Product): string[] {
    return this.parseFeatureAsArray(this.getFeatureForProduct(product, WORKS_WITH_NAME_FEATURE));
  }

  /**
   * Retrieve the solution types array from a product's classifications
   *
   * @param product the product where to search for the description
   */
  getSolutionTypes(product: Product): string[] {
    return this.parseFeatureAsArray(this.getFeatureForProduct(product, SOLUTION_TYPES_NAME_FEATURE));
  }

  /**
   * Retrieve the meta keywords from a product's classifications
   *
   * @param product the product where to search for the meta keywords
   */
  getMetaKeywords(product: Product): string {
    return this.getFeatureForProduct(product, META_KEYWORDS_FEATURE);
  }

  /**
   * Get the product's feature value by feature name.
   *
   * @param product the product where to search for classifications
   * @param featureName name of the feature to search
   */
  getFeatureForProduct(product: Product, featureName: string): string {
    if (product && product.classifications && product.classifications.length) {
      const features: Feature[] = product.classifications[0].features;
      const feature: Feature = features.find(f => f.name === featureName);
      return feature?.featureValues[0]?.value;
    }
  }

  /**
   * Get the url of the certification's icon
   *
   * @param product the certified product
   */
  getCertificationIconUrl(product: Product): string {
    const certificationTitle: string[] = this.parseFeatureAsArray(this.getCertification(product));
    if (certificationTitle.length > 0) {
      return this.getCertificationIconFromFacet(certificationTitle[0]);
    }
    return null;
  }

  /**
   * Parse a string as an array
   *
   * @param featureStr the string of multiple values
   */
  parseFeatureAsArray(featureStr: string): string[] {
    if (!featureStr) {
      return [];
    }
    featureStr = featureStr.replace('[', '');
    featureStr = featureStr.replace(']', '');
    return featureStr.split(', ');
  }

  /**
   * Retrieve the certification icon from the facets
   *
   * @param certificationTitle the specific certification
   */
  getCertificationIconFromFacet(certificationTitle: string): string {
    if (this.backendFacets && certificationTitle) {
      const certificationFacet: Facet = this.backendFacets.find(facet => facet.name === CERTIFICATION_NAME_FACET);
      if (!!certificationFacet) {
        const certificationFacetValue: DcpFacetValue =
          certificationFacet.values.find(value => value.name === certificationTitle);

        const hasImageUrl: boolean = !!certificationFacetValue?.image?.url;

        return hasImageUrl ? this.configService.getString('backend.occ.baseUrl') + certificationFacetValue.image.url : undefined;
      }
    }
    return undefined;
  }

  /**
   * Concatenate the id of a permutation product.
   *
   * @param cId Json Object extracted from the data array of a product.
   */
  getPermutationProductId(cId: CanonicalId): string {
    return [
      cId?.productId?.id,
      cId?.productId?.catalog,
      cId?.productId?.version,
      cId?.entitlementRuleId,
      cId?.commercialModel?.code,
      cId?.technicalAssetVariantId,
      cId?.technicalAssetId?.id
    ].filter(Boolean).join(SEPARATOR);
  }

  /**
   * Returns the commercialModel.
   *
   * @param product product to extract the commercialModel from.
   * @param bundleTemplate selected bundleTemplate to extract the commercialModel from.
   * If the Commercial model linked to the bundle template is not present, the JsonData of product are read.
   */
  getCommercialModel(product: Product, bundleTemplate?: DcpBundleTemplate): CommercialModel {
    return this.plansAndPricingDataService.getCommercialModel(bundleTemplate?.data ?? product.data);
  }

  /**
   * Returns the id of a product. e.g. : a0c6139f-23f2-44df-a6f1-ef5e8d33d0b3
   *
   * @param product product to get the id of.
   */
  getProductId(product: Product): string {
    const canonicalId: CanonicalId = this.getCanonicalId(product);
    if (canonicalId?.productId?.id !== '') {
      return canonicalId.productId.id;
    }
    return '';
  }

  /**
   * Get product ID used for fetching reviews e.g. : a0c6139f-23f2-44df-a6f1-ef5e8d33d0b3
   *
   * @param product product to get the id of.
   */
  getProductReviewsId(product: Product): string {
    if (product.data) {
      const productReviewSection: TypeValuePair = product.data.find(entry => entry.type === REVIEWS_DATA_SECTION_ID);
      if (productReviewSection) {
        const reviews: DcpReviews = this.jsonService.parseSafely(productReviewSection.value) as DcpReviews;
        if (reviews?.productId) {
          return reviews.productId;
        }
      }
    }
    return '';
  }

  /**
   * Returns the product.images.PRIMARY.image object.
   *
   * @param product to get the image of.
   */
  getPrimaryImg(product: Product): Image {
    if (product?.images?.length) {
      return (product.images as any).find(image => image.imageType === 'PRIMARY');
    } else if (product?.images?.PRIMARY?.hasOwnProperty('image')) {
      return (product.images.PRIMARY as any).image;
    } else {
      return undefined;
    }
  }

  /**
   * Concatenates product.name and product.supplierAlternativeAID
   *
   * @param product DcpProduct
   */
  getNameAndSupplierAlternativeAid(product: Product): string {
    if (!product) {
      return '';
    }
    return product.name +
      (product.supplierAlternativeAID ? ' (' + product.supplierAlternativeAID + ')' : '');
  }

  /**
   * Returns the ExternalUrl (free trial url)
   *
   * @param bundleTemplate Determines which external url is read from the product data.
   */
  getExternalUrl(bundleTemplate?: DcpBundleTemplate): string {
    if (bundleTemplate?.data) {
      const externalUrl: string = this.jsonService.parseSafely(
        this.jsonService.findDataSet(bundleTemplate.data, EXTERNAL_URL).value) as string;
      if (externalUrl !== 'null') {
        return externalUrl;
      }
    }
    return undefined;
  }

  /**
   * Fetches a bundle template by canonical ID for a given product.
   * @param {Product} product - The product object for which to fetch the bundle template.
   * @param {string} materialId - The material ID of the template.
   */
  fetchBundleTemplateByCanonicalId(product: Product, materialId: string): DcpBundleTemplate {
    return product.bundleTemplates?.find((bundleTemplate) => {
      const canonicalId = this.getCanonicalId(product, bundleTemplate);
      return canonicalId?.entitlementRuleCode?.includes(materialId);
    });
  }

  /**
   * Sets params from SessionStorage to URL
   * 
   * @param {string} url - URL which should be updated
   */
  setParamsToUrl(url: string): URL {
    const originURL = new URL(url);
    this.crmAccountTrackingService.getAllParametersArray().forEach(param => {
      const paramValue = this.crmAccountTrackingService.getDataFromSessionStorage(param);
      if (paramValue) {
        originURL.searchParams.set(param, paramValue);
      }
    });
    const campaignCode = this.campaignTrackingService.getCampaignData()?.id;
    if (campaignCode) {
      originURL.searchParams.set('campaigncode', campaignCode);
    }
    return originURL;
  }

  private isValidUrl(url: string) {
    try {
      return new URL(url);
    } catch (_) {
      return false;
    }
  }

  isExcludedFromSearch(product: Product) {
    return product?.categories?.some((c) => c.code === 'exclude-from-search')
  }
}
